import { DefaultOrganizationId } from '@/constants/defaultOrganizationId';
import { SidebarItems, type SidebarItem } from '@/constants/sidebarItems';
import { UserClaim } from '@/constants/userClaim';
import { clearCookies, getAccessToken, setUserCredentials } from '@/utils/cookieHelpers';
import { AdminClaim, decodeAccessToken } from '@/utils/jwtHelpers';
import { deepCloneObject } from '@/utils/objectManipulation';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useConfigStore } from '@/stores/configStore';
import { API_ROUTES } from '@/constants/apiRoutes';
import { getAvatarId, hasChildOrganizations, login } from '@/services/security/SecurityService';
import { type UserCredentials } from '@/services/security/models/user';
import { RequestStatus } from '@/core/api/types';
import type { RouteDefinition, RouteRedirection } from '@/router/types';
import { authorizedRoutes } from '@/router/routes/authorizedRoutes';
import { useTranslation } from '@/plugins/i18n/useTranslation';
import { ChildOrganizationsRouteNames, RouteName, type ChildOrganizationRoute } from '@/router/constants/routeNames';
import { useRoute, useRouter } from 'vue-router';
import { useAlertStore } from './alertStore';
import { isUnauthorizedRoute } from '@/router/routes/unauthorizedRoutes';

export type AuthState = {
  isAuth: boolean;
  user: {
    login: string;
    id: number;
    nameAndSurname: string;
    claims: UserClaim[];
    budgetNumber: string;
    parentOrganizationRegistrationId: string;
    organizationId?: string;
    hasChildOrganisations?: boolean;
  };
  organizationId: number;
  expirationDate: number | null;
  lastActivity: number | null;
  userAvatar?: string | null;
};

const defaultState: AuthState = {
  isAuth: false,
  user: {
    login: '',
    id: 0,
    nameAndSurname: '',
    claims: [],
    budgetNumber: '',
    parentOrganizationRegistrationId: ''
  },
  organizationId: 0,
  expirationDate: null,
  lastActivity: null
} as const;

export const useAuthStore = defineStore(
  'auth',
  () => {
    const router = useRouter();
    const route = useRoute();
    const { t } = useTranslation();
    const { removeErrorAlerts } = useAlertStore();
    const { apiRoot } = useConfigStore();
    const authState = ref<AuthState>(deepCloneObject(defaultState));
    const accessToken = ref('');

    const isAuthenticated = computed(() => authState.value.isAuth);
    const isAdmin = computed(() => {
      if (!authState.value.user?.claims?.length) {
        return false;
      }
      return authState.value.user.claims.some(
        (claim: UserClaim) => claim === UserClaim.Claim_Admin
      );
    });

    const isAdministration = computed(
      () => authState.value.user.organizationId === DefaultOrganizationId
    );

    const sidebarItems = computed(() => {
      if (!authState.value.isAuth) {
        return [];
      }
      const items = setSidebarItems(
        deepCloneObject(SidebarItems),
        !!authState.value.user.hasChildOrganisations
      );
      return items;
    });

    const avatarId = computed(() => authState.value.userAvatar);

    const userId = computed(() => authState.value.user.id);

    const organizationId = computed(() => authState.value.organizationId);

    const userClaims = computed(() => authState.value.user.claims);

    const userInitials = computed(() => {
      const name = authState.value.user.nameAndSurname;
      const initials = name
        .split(' ')
        .map((n) => n[0])
        .join(' ');
      return initials;
    });

    const avatarUrl = computed(() =>
      authState.value.userAvatar
        ? `${apiRoot}/${API_ROUTES.Security.AvatarUrl(authState.value.user.login || '', authState.value.userAvatar || '')}`
        : ''
    );

    function setSidebarItems(items: SidebarItem[], hasChildOrganisations: boolean): SidebarItem[] {
      const mapped = items
        .map((item: SidebarItem): SidebarItem | null =>
          checkIfUserHasClaim(item, hasChildOrganisations)
        )
        .filter((item): item is SidebarItem => !!item);
      if (hasChildOrganisations) {
        return mapped;
      }
      return mapped.filter((item): boolean => !item.dataCy.includes('subordinate'));
    }

    function checkIfUserHasClaim(
      item: SidebarItem,
      hasChildOrganisations: boolean
    ): SidebarItem | null {
      if (item.type === 'parent') {
        item.children = setSidebarItems(item.children, hasChildOrganisations);
      }
      if (!item.claims?.length) {
        return item;
      } else {
        if (!checkClaims(item.claims, authState.value.user.claims)) {
          return null;
        }
        return item;
      }
    }

    function checkClaims(requiredClaims: UserClaim[], userClaims: UserClaim[]): boolean {
      return requiredClaims.some((claim: UserClaim) => userClaims.includes(claim));
    }

    function hasClaim(claim: UserClaim): boolean {
      return (
        userClaims.value.includes(claim) || userClaims.value.includes(AdminClaim as UserClaim)
      );
    }

    function hasRedirectPrivileges(redirection: RouteRedirection): boolean {
      const selectedRoute: RouteDefinition | undefined = authorizedRoutes(t).find((r) => r.name === redirection.name);
      if (!selectedRoute) {
        return false;
      }
      if (ChildOrganizationsRouteNames.includes(selectedRoute.name as ChildOrganizationRoute)) {
        return !!authState.value.user.hasChildOrganisations;
      }
      return !selectedRoute.meta?.claims?.length || selectedRoute.meta.claims.some((claim: UserClaim) => hasClaim(claim));
    }

    function setAuthState(state: typeof defaultState): void {
      authState.value = { ...authState.value, ...state };
    }

    function setCredentials(token: string): void {
      accessToken.value = token;
      const decodedToken = decodeAccessToken(token);
      if (!decodedToken) {
        return;
      }
      const newState: AuthState = {
        isAuth: true,
        user: {
          login: decodedToken.unique_name,
          nameAndSurname: decodedToken.given_name,
          id: +decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid'],
          claims:
            typeof decodedToken.security_claims === 'string'
              ? [decodedToken.security_claims]
              : (decodedToken.security_claims as UserClaim[]),
          organizationId: decodedToken.ORGANIZATION_BUDGET_ID_NUMBER,
          parentOrganizationRegistrationId: decodedToken.PARENT_ORGANIZATION_ID,
          budgetNumber: decodedToken.ORGANIZATION_BUDGET_ID_NUMBER
        },
        organizationId: +decodedToken.groupsid,
        expirationDate: decodedToken.exp ? decodedToken.exp * 1000 : null,
        lastActivity: Date.now()
      };

      setAuthState(newState);
    }

    function refreshActivity(): void {
      authState.value.lastActivity = Date.now();
    }

    async function onLogin(credentials: UserCredentials): Promise<RequestStatus> {
      const response = await login(credentials);
      if (response.status === RequestStatus.ERROR) {
        return response.status;
      }
      setUserCredentials(credentials.login, response.data.accessToken, response.data.refreshToken);
      await fetchAndSetUserData(response.data.accessToken);
      removeErrorAlerts();
      return response.status;
    }

    async function fetchAndSetUserData(accessToken: string): Promise<void> {
      await fetchAndSetAvatarAndChildOrganizations(accessToken);
    }

    async function fetchAndSetAvatarAndChildOrganizations(accessToken: string) {
      const [avatar, childOrganisationsResponse] = await Promise.all([
        getAvatarId(),
        hasChildOrganizations()
      ]);
      if (accessToken) {
        setCredentials(accessToken);
      }
      setAvatarId(avatar.data);
      setChildOrganisationsExistance(!!childOrganisationsResponse.data?.hasChildOrganizations);
    }

    function onLogout(): void {
      clearState();
    }

    function clearState(): void {
      setAuthState(deepCloneObject(defaultState));
      accessToken.value = '';
      clearCookies();
      if (isUnauthorizedRoute(route.name as string)) {
        return;
      }
      router.push({ name: RouteName.Home });
    }

    function setAvatarId(avatar: string | null) {
      authState.value.userAvatar = avatar;
    }

    function setChildOrganisationsExistance(hasChildOrganisations: boolean) {
      authState.value.user.hasChildOrganisations = hasChildOrganisations;
    }

    async function compareTokens(): Promise<void> {
      const cookieToken = getAccessToken();
      if (!cookieToken) {
        onLogout();
        return;
      }
      if (cookieToken !== accessToken.value) {
        await fetchAndSetUserData(cookieToken);
      }
    }

    return {
      isAuthenticated,
      authState,
      accessToken,
      isAdmin,
      isAdministration,
      sidebarItems,
      avatarId,
      avatarUrl,
      userInitials,
      userClaims,
      setAvatarId,
      setSidebarItems,
      setAuthState,
      organizationId,
      userId,
      refreshActivity,
      checkIfUserHasClaim,
      checkClaims,
      setCredentials,
      onLogin,
      onLogout,
      hasClaim,
      hasRedirectPrivileges,
      setChildOrganisationsExistance,
      compareTokens,
      fetchAndSetAvatarAndChildOrganizations
    };
  },
  { persist: true }
);
